import { useCallback, useMemo } from 'react';
import { logError } from '../../../lib/logError';
import { BillingCycle, SubscriptionTier, ResellingPrograms, PricingDetails, BillingTerm } from '../../../type';

interface UseSubscriptionDetailsSliceArgs {
    currentCountry: string;
    products: ResellingPrograms['products'];
}

export const useSubscriptionDetailsSlice = ({ currentCountry, products }: UseSubscriptionDetailsSliceArgs) => {
    const subscriptionDetails = useMemo(
        () => getSubscriptionDetails({ currentCountry, products }),
        [currentCountry, products]
    );

    const getPricingDetailsByBillingCycle = useCallback(
        (cycle: BillingCycle) => subscriptionDetails.get(cycle),
        [subscriptionDetails]
    );

    const getSubscriptionDetailsByTier = useCallback(
        (tierName: SubscriptionTier) => {
            const result = new Map<BillingCycle, PricingDetails>();

            subscriptionDetails.forEach((details, cycle) => {
                const pricingDetails = details.find((detail) => detail.tierName === tierName);

                if (pricingDetails) {
                    result.set(cycle, pricingDetails);
                }
            });

            return result;
        },
        [subscriptionDetails]
    );

    return { subscriptionDetails, getPricingDetailsByBillingCycle, getSubscriptionDetailsByTier };
};

function getSubscriptionDetails({ currentCountry, products }: UseSubscriptionDetailsSliceArgs) {
    const defaultProducts = products.DEFAULT ?? {};
    const countryProducts = products[currentCountry] ?? defaultProducts;

    const subscriptionDetails = new Map<BillingCycle, PricingDetails[]>();

    const billingCycleList = [BillingCycle.MONTHLY, BillingCycle.YEARLY];
    const subscriptionTiers = [
        SubscriptionTier.Basic,
        SubscriptionTier.Standard,
        SubscriptionTier.Premium,
        SubscriptionTier.Apps,
    ];

    billingCycleList.forEach((cycle) => {
        const defaultTiers = defaultProducts[cycle] ?? {};
        const countryTiers = countryProducts[cycle] ?? defaultTiers;

        const pricingDetails: PricingDetails[] = subscriptionTiers.map((tierName) => {
            const countryTier =
                countryTiers.find((tierItem) => tierItem.name === tierName) ??
                defaultTiers.find((tierItem) => tierItem.name === tierName);

            if (!countryTier) {
                logError(new Error(`Subscription Tier: ${tierName} not found`));

                return {
                    productId: null,
                    tierName,
                    billingCycle: cycle,
                    billingTerm: BillingTerm.MONTH,
                    minQuantity: 0,
                    maxQuantity: 0,
                    totalQuantity: 0,
                    price: null,
                } satisfies PricingDetails;
            }

            return {
                productId: countryTier.id,
                tierName: countryTier.name,
                billingCycle: countryTier.billingCycle,
                billingTerm: countryTier.billingTerm,
                minQuantity: countryTier.minQuantity,
                maxQuantity: countryTier.maxQuantity,
                totalQuantity: countryTier.totalQuantity,
                price: {
                    ...countryTier.price,
                    amountMonthly: countryTier.price.monthlyAmountInCents / 100,
                    amountYearly: countryTier.price.amountInCents / 100,
                    discountedAmountMonthly: countryTier.price.discountedMonthlyAmountInCents / 100,
                    discountedAmountYearly: countryTier.price.discountedAmountInCents / 100,
                },
            } satisfies PricingDetails;
        });

        subscriptionDetails.set(cycle, pricingDetails);
    });

    return subscriptionDetails;
}
